"use client";

import { assetUrl } from "@/lib/asset-url";
import { cn } from "@/lib/utils";

interface WexioMarkProps {
  /** Pixel size of the rendered square. */
  size?: number;
  className?: string;
}

/**
 * Wexio brand mark. Used in:
 *   - Header chrome (top-left of the widget panel)
 *   - Recent-message preview avatar (AI/bot senders without a custom avatar)
 *   - Bot-authored chat bubbles
 *
 * Theme-aware: ships two assets that swap via Tailwind's `dark:`
 * variant (which the widget routes through `[data-theme="dark"]`
 * via `@custom-variant dark`).
 *   • `logo-dark.png`  — dark glyph for light surfaces (light theme)
 *   • `logo-light.png` — light glyph for dark surfaces (dark theme)
 * No JS / state — the panel root's `data-theme` attribute drives
 * visibility, so flipping theme mid-session swaps the mark instantly.
 *
 * Plain `<img>` (not `next/image`): the widget ships as a Vite
 * library bundle with no Next runtime, and the asset path routes
 * through `assetUrl` so the React-component embed loads the logo
 * from the widget's CDN instead of the host page's origin.
 */
export function WexioMark({ size = 24, className }: WexioMarkProps) {
  return (
    <span
      style={{ width: size, height: size }}
      className={cn("relative inline-block", className)}
      role="img"
      aria-label="Wexio"
    >
      {/* biome-ignore lint/performance/noImgElement: bundled brand asset; next/image's loader isn't available in the web-component / iframe embeds */}
      <img
        src={assetUrl("/logo-dark.png")}
        alt=""
        width={size}
        height={size}
        className="block h-full w-full dark:hidden"
      />
      {/* biome-ignore lint/performance/noImgElement: bundled brand asset; next/image's loader isn't available in the web-component / iframe embeds */}
      <img
        src={assetUrl("/logo-light.png")}
        alt=""
        width={size}
        height={size}
        className="hidden h-full w-full dark:block"
      />
    </span>
  );
}
